import { useState, useEffect } from 'react';
import AdminLayout from '../../components/admin/AdminLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import axios from 'axios';
import { Plus, Edit, Trash2, Tag, X, Percent, DollarSign } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminPromoCodes() {
  const [promoCodes, setPromoCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    code: '', description: '', discount_type: 'percentage',
    discount_value: '', min_order: '0', max_discount: '',
    usage_limit: '0', is_active: true, expires_at: ''
  });

  const token = localStorage.getItem('admin_token');
  const headers = { Authorization: `Bearer ${token}` };

  const fetchPromoCodes = async () => {
    try {
      const res = await axios.get(`${API}/admin/promo-codes`, { headers });
      setPromoCodes(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchPromoCodes(); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const resetForm = () => {
    setFormData({
      code: '', description: '', discount_type: 'percentage',
      discount_value: '', min_order: '0', max_discount: '',
      usage_limit: '0', is_active: true, expires_at: ''
    });
    setEditingId(null);
    setShowForm(false);
  };

  const handleEdit = (promo) => {
    setFormData({
      code: promo.code,
      description: promo.description || '',
      discount_type: promo.discount_type,
      discount_value: String(promo.discount_value),
      min_order: String(promo.min_order || 0),
      max_discount: promo.max_discount ? String(promo.max_discount) : '',
      usage_limit: String(promo.usage_limit || 0),
      is_active: promo.is_active,
      expires_at: promo.expires_at ? promo.expires_at.split('T')[0] : ''
    });
    setEditingId(promo.id);
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.code || !formData.discount_value) {
      toast.error('Code and discount value are required');
      return;
    }

    const payload = {
      ...formData,
      discount_value: parseFloat(formData.discount_value),
      min_order: parseFloat(formData.min_order) || 0,
      max_discount: formData.max_discount ? parseFloat(formData.max_discount) : null,
      usage_limit: parseInt(formData.usage_limit) || 0,
      expires_at: formData.expires_at ? new Date(formData.expires_at).toISOString() : null
    };

    try {
      if (editingId) {
        await axios.put(`${API}/admin/promo-codes/${editingId}`, payload, { headers });
        toast.success('Promo code updated');
      } else {
        await axios.post(`${API}/admin/promo-codes`, payload, { headers });
        toast.success('Promo code created');
      }
      resetForm();
      fetchPromoCodes();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save promo code');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this promo code?')) return;
    try {
      await axios.delete(`${API}/admin/promo-codes/${id}`, { headers });
      toast.success('Promo code deleted');
      fetchPromoCodes();
    } catch (err) {
      toast.error('Failed to delete');
    }
  };

  const handleToggle = async (promo) => {
    try {
      await axios.put(`${API}/admin/promo-codes/${promo.id}`, { is_active: !promo.is_active }, { headers });
      fetchPromoCodes();
    } catch (err) {
      toast.error('Failed to update');
    }
  };

  if (loading) {
    return (
      <AdminLayout title="Promo Codes" subtitle="Manage promotional discounts">
        <div className="flex justify-center py-20">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-fire-500" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="Promo Codes" subtitle="Create and manage promotional discounts">
      <div data-testid="admin-promo-codes-page">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <p className="text-gray-400">{promoCodes.length} promo code{promoCodes.length !== 1 ? 's' : ''}</p>
          </div>
          <Button onClick={() => { resetForm(); setShowForm(true); }} className="bg-fire-500 hover:bg-fire-600 rounded-full" data-testid="add-promo-btn">
            <Plus className="w-4 h-4 mr-2" /> New Promo Code
          </Button>
        </div>

        {/* Form Modal */}
        {showForm && (
          <div className="bg-card rounded-2xl border border-white/10 p-6 mb-8" data-testid="promo-form">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-white font-bold text-xl">{editingId ? 'Edit' : 'Create'} Promo Code</h3>
              <button onClick={resetForm} className="text-gray-400 hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-400 mb-2 block">Code *</Label>
                  <Input value={formData.code} onChange={e => setFormData({...formData, code: e.target.value.toUpperCase()})} placeholder="WELCOME10" className="bg-black/50 border-white/10 text-white uppercase" data-testid="promo-code-input" />
                </div>
                <div>
                  <Label className="text-gray-400 mb-2 block">Description</Label>
                  <Input value={formData.description} onChange={e => setFormData({...formData, description: e.target.value})} placeholder="10% off first order" className="bg-black/50 border-white/10 text-white" data-testid="promo-description-input" />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label className="text-gray-400 mb-2 block">Discount Type</Label>
                  <div className="flex gap-2">
                    <Button type="button" onClick={() => setFormData({...formData, discount_type: 'percentage'})} className={`flex-1 rounded-lg ${formData.discount_type === 'percentage' ? 'bg-fire-500' : 'bg-black/50 border border-white/10 text-gray-400'}`} data-testid="type-percentage">
                      <Percent className="w-4 h-4 mr-1" /> %
                    </Button>
                    <Button type="button" onClick={() => setFormData({...formData, discount_type: 'fixed'})} className={`flex-1 rounded-lg ${formData.discount_type === 'fixed' ? 'bg-fire-500' : 'bg-black/50 border border-white/10 text-gray-400'}`} data-testid="type-fixed">
                      <DollarSign className="w-4 h-4 mr-1" /> Fixed
                    </Button>
                  </div>
                </div>
                <div>
                  <Label className="text-gray-400 mb-2 block">Discount Value *</Label>
                  <Input type="number" value={formData.discount_value} onChange={e => setFormData({...formData, discount_value: e.target.value})} placeholder={formData.discount_type === 'percentage' ? '10' : '500'} className="bg-black/50 border-white/10 text-white" data-testid="promo-value-input" />
                </div>
                <div>
                  <Label className="text-gray-400 mb-2 block">Min Order (₦)</Label>
                  <Input type="number" value={formData.min_order} onChange={e => setFormData({...formData, min_order: e.target.value})} placeholder="0" className="bg-black/50 border-white/10 text-white" data-testid="promo-min-order" />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {formData.discount_type === 'percentage' && (
                  <div>
                    <Label className="text-gray-400 mb-2 block">Max Discount (₦)</Label>
                    <Input type="number" value={formData.max_discount} onChange={e => setFormData({...formData, max_discount: e.target.value})} placeholder="No limit" className="bg-black/50 border-white/10 text-white" data-testid="promo-max-discount" />
                  </div>
                )}
                <div>
                  <Label className="text-gray-400 mb-2 block">Usage Limit (0 = unlimited)</Label>
                  <Input type="number" value={formData.usage_limit} onChange={e => setFormData({...formData, usage_limit: e.target.value})} placeholder="0" className="bg-black/50 border-white/10 text-white" data-testid="promo-usage-limit" />
                </div>
                <div>
                  <Label className="text-gray-400 mb-2 block">Expires On</Label>
                  <Input type="date" value={formData.expires_at} onChange={e => setFormData({...formData, expires_at: e.target.value})} className="bg-black/50 border-white/10 text-white" data-testid="promo-expires" />
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-2">
                <Button type="button" variant="outline" onClick={resetForm} className="border-white/10 text-white hover:bg-white/10 rounded-full">Cancel</Button>
                <Button type="submit" className="bg-fire-500 hover:bg-fire-600 rounded-full" data-testid="promo-save-btn">
                  {editingId ? 'Update' : 'Create'} Promo Code
                </Button>
              </div>
            </form>
          </div>
        )}

        {/* Promo Codes List */}
        {promoCodes.length === 0 ? (
          <div className="bg-card rounded-2xl border border-white/10 p-12 text-center">
            <Tag className="w-16 h-16 text-gray-600 mx-auto mb-4" />
            <h3 className="text-xl text-white mb-2">No promo codes yet</h3>
            <p className="text-gray-400">Create your first promotional discount</p>
          </div>
        ) : (
          <div className="space-y-4">
            {promoCodes.map(promo => (
              <div key={promo.id} className="bg-card rounded-xl border border-white/10 p-5 flex flex-col md:flex-row md:items-center gap-4" data-testid={`promo-${promo.id}`}>
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-1">
                    <span className="font-mono font-bold text-fire-500 text-lg">{promo.code}</span>
                    <Badge className={promo.is_active ? 'bg-green-500/20 text-green-400 border-green-500/50 border' : 'bg-gray-500/20 text-gray-400 border-gray-500/50 border'}>
                      {promo.is_active ? 'Active' : 'Inactive'}
                    </Badge>
                    <Badge className="bg-blue-500/20 text-blue-400 border-blue-500/50 border">
                      {promo.discount_type === 'percentage' ? `${promo.discount_value}% off` : `₦${promo.discount_value.toLocaleString()} off`}
                    </Badge>
                  </div>
                  <p className="text-gray-400 text-sm">{promo.description}</p>
                  <div className="flex flex-wrap gap-4 mt-2 text-xs text-gray-500">
                    {promo.min_order > 0 && <span>Min: ₦{promo.min_order.toLocaleString()}</span>}
                    {promo.max_discount && <span>Max discount: ₦{promo.max_discount.toLocaleString()}</span>}
                    <span>Used: {promo.times_used}{promo.usage_limit > 0 ? `/${promo.usage_limit}` : ''}</span>
                    {promo.expires_at && <span>Expires: {new Date(promo.expires_at).toLocaleDateString()}</span>}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button size="sm" variant="outline" onClick={() => handleToggle(promo)} className="border-white/10 text-gray-400 hover:bg-white/10 rounded-lg" data-testid={`toggle-${promo.id}`}>
                    {promo.is_active ? 'Disable' : 'Enable'}
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => handleEdit(promo)} className="border-white/10 text-white hover:bg-white/10 rounded-lg" data-testid={`edit-${promo.id}`}>
                    <Edit className="w-4 h-4" />
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => handleDelete(promo.id)} className="border-red-500/30 text-red-400 hover:bg-red-500/20 rounded-lg" data-testid={`delete-${promo.id}`}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
